import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import uniqueId from 'lodash/uniqueId';
import * as ramda from 'ramda';

import type {Delay} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import {sortByPosition} from 'nutshell-core/utils/sorting/sort-by-position';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {Dialog} from 'shells/dialog/dialog';
import {scrubEmailHtmlPlaceholders} from 'shells/email-editor/placeholder-utils';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {ProBadgeIcon} from 'shells/icon/svg/pro-badge';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {FooterBar} from 'shells/modal/footer-bar';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {
    GetEmailSequenceTemplate_emailSequenceTemplate as EmailSequenceTemplate,
    EmailSequenceTemplateTemplate,
    GetSessionUser_loggedInUser as User,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {EmailFeatureGateModal} from '../../limits/feature-gates/email-feature-gate-modal';

import {hasEmailSequenceAutomation} from '../has-email-sequence-automation';
import {hasEmailSequenceCapability} from '../has-email-sequence-capability';

import {addReplySubjects} from './add-reply-subjects';
import {
    createSequenceTemplate,
    createNormalizedSequenceTemplate,
    PLACEHOLDER_ID,
} from './create-empty-sequence-template';
import {EmailTemplateDesignerEditor} from './email-template-designer-editor';
import {
    getFirstEmailTemplateId,
    getStateFromSequenceTemplate,
    canChangeTemplateGroup,
} from './email-utils';
import {ShareableEmailTemplatesUpsell} from './shareable-email-templates-upsell';
import {useGetEmailTemplateGroups} from './template-groups/graphql/hooks/use-get-email-template-groups';
import {TemplateGroupPicker} from './template-groups/template-group-picker';
import type {EmailTemplate} from './types';

import styles from './email-template-designer-form-component.module.css';

const DEFAULT_DELAY = 3;

export type CreateSequenceTemplate = (arg1: {
    name: string;
    isShared: boolean;
    groupId: string | null | undefined;
    parentEmailSequenceTemplateId?: string;
    emailSequenceTemplateMessages: EmailTemplate[];
}) => Promise<any>;

export type UpdateSequenceTemplate = (arg1: {
    id: string;
    name: string;
    isShared: boolean;
    groupId: string | null | undefined;
    emailSequenceTemplateMessages: EmailTemplate[];
}) => Promise<any>;

type Props = {
    closeModal: () => void;
    onChildTemplateSaved: () => void;
    createSequenceTemplate: CreateSequenceTemplate;
    updateSequenceTemplate: UpdateSequenceTemplate;
    isLoading: boolean;
    // The `root` email sequence template. This is either a parent with an array
    // of children, or is itself a child, and the parent has an array of
    // children
    emailSequenceTemplate: EmailSequenceTemplate | null | undefined;
    onSetShowProInfo: (value: boolean) => void;
    shouldShowProInfo: boolean;
    startEditingSharedTemplate: () => void;
    onUserChildTemplateDirtied: () => void;
    undeleteSequenceTemplate: (templateId: string) => Promise<any>;
    groupId?: string | null | undefined;
    refetchGroups?: () => Promise<any>;
};

type State = {
    isCollectingName: boolean;
    isSaving: boolean;
    hasInitialized: boolean;
    name: string;
    selectedTemplateId: string;
    selectedSequenceId: string;
    emailSequenceTemplates: {
        [sequenceId: string]: EmailSequenceTemplateTemplate & {
            emailSequenceTemplateMessages: {
                [templateId: string]: EmailTemplate;
            };
        };
    };
    sortedEmailSequenceTemplates: {
        id: string;
        name: string;
    }[];
    isShared: boolean;
    errorMessage: string | null | undefined;
    // Can't really get a "dirty" state about the current user's sequence
    // template because of how we're shimming in blank ones and our state, so
    // just use this to keep track of if the user's sequence changes
    isUsersSequenceDirty: boolean;
    isShowingCloseDialog: boolean;
    templateGroupId: string;
};

type ClassProps = Props & {
    groups: any[];
    user: User | null | undefined;
    toastState: SavedSettingToastState;
    setIsSaving: (isSaving: boolean) => void;
};

/*
 * This is the top-level component for creating or editing an email sequence template.
 */
class EmailTemplateDesignerFormClassComponent extends React.PureComponent<ClassProps, State> {
    saveButton: HTMLElement | null | undefined;
    cancelButtonRef: HTMLElement | null | undefined;

    constructor(props: ClassProps) {
        super(props);
        const {isLoading, emailSequenceTemplate, groupId} = props;

        let state = {
            isCollectingName: false,
            isSaving: false,
            emailTemplates: {},
            hasInitialized: false,
            errorMessage: null,
            isUsersSequenceDirty: false,
            isShowingCloseDialog: false,
        };

        if (emailSequenceTemplate && !isLoading) {
            const templateState = getStateFromSequenceTemplate(emailSequenceTemplate);
            state = {
                ...state,
                ...templateState,
                hasInitialized: true,
                // @ts-expect-error
                isShared: emailSequenceTemplate.isShared,
                templateGroupId: emailSequenceTemplate.group
                    ? emailSequenceTemplate.group.id
                    : 'null',
            };
        } else {
            const defaultTemplate = createSequenceTemplate();
            const loadingState = getStateFromSequenceTemplate(defaultTemplate);
            state = {
                ...state,
                ...loadingState,
                // @ts-expect-error
                isShared: defaultTemplate.isShared,
                templateGroupId: 'null',
            };
        }

        if (groupId) {
            state = {
                ...state,
                // @ts-expect-error
                templateGroupId: groupId,
            };
        }

        // @ts-expect-error
        this.state = state;
    }

    componentDidMount() {
        const template = this.getActiveTemplate();
        if (template) {
            this.validateTemplate(template);
        }
        this.addFakeSubjects();
    }

    componentDidUpdate() {
        const template = this.getActiveTemplate();

        // @ts-expect-error
        this.validateTemplate(template);
    }

    // We'd love to use getDerivedStateFromProps here, but our older version of
    // react-apollo crashes if we do.
    UNSAFE_componentWillReceiveProps(nextProps: ClassProps) {
        // If the modal stays open, but we switch to a new root email sequence,
        // we should update our state. This happens when we change the sequence
        // id in the URL
        const hasEmailSequenceTemplateChanged =
            this.props.emailSequenceTemplate &&
            nextProps.emailSequenceTemplate &&
            this.props.emailSequenceTemplate.id !== nextProps.emailSequenceTemplate.id;

        if (this.state.hasInitialized && !hasEmailSequenceTemplateChanged) return;

        const {emailSequenceTemplate, isLoading} = nextProps;

        if (emailSequenceTemplate && !isLoading) {
            const templateState = getStateFromSequenceTemplate(emailSequenceTemplate);
            this.setState({
                ...templateState,
                hasInitialized: true,
                isShared: emailSequenceTemplate.isShared,
                templateGroupId: emailSequenceTemplate.group
                    ? emailSequenceTemplate.group.id
                    : 'null',
            });
            this.addFakeSubjects();
        }
    }

    render() {
        if (this.props.shouldShowProInfo) {
            return (
                <EmailFeatureGateModal
                    isOpen={this.props.shouldShowProInfo}
                    onClose={() => {
                        this.props.onSetShowProInfo(false);
                    }}
                />
            );
        }

        const canChangeEmailTemplateGroup = canChangeTemplateGroup(
            this.props.user,
            this.props.emailSequenceTemplate,
            this.newIsEditingChildTemplate()
        );

        return (
            <form
                onSubmit={this.submitForm}
                className={styles['email-template-designer-form-component']}
            >
                <EmailTemplateDesignerEditor
                    isLoading={this.props.isLoading}
                    readOnly={
                        this.isViewingSharedSequenceTemplate() ||
                        this.isViewingArchivedSequenceTemplate()
                    }
                    selectedTemplateId={this.state.selectedTemplateId}
                    isUsersSequenceDirty={this.state.isUsersSequenceDirty}
                    emailTemplates={
                        // @ts-expect-error
                        this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                            .emailSequenceTemplateMessages
                    }
                    sortedEmailSequenceTemplates={this.state.sortedEmailSequenceTemplates}
                    selectedEmailSequenceTemplateId={this.state.selectedSequenceId}
                    showEmailSequenceTemplateList={Boolean(
                        this.props.emailSequenceTemplate &&
                            this.props.emailSequenceTemplate.isShared &&
                            this.props.emailSequenceTemplate.id !== PLACEHOLDER_ID
                    )}
                    addEmail={this.addEmail}
                    removeEmail={this.hasMultipleMessageTemplates() ? this.removeEmail : undefined}
                    onEmailSelect={this.handleEmailSelect}
                    onSubjectChange={this.handleSubjectChange}
                    onBodyChange={this.handleBodyChange}
                    onBodyLoad={this.handleBodyLoad}
                    onSendAsReplyChange={this.handleSendAsReplyChange}
                    onUseSignatureChange={this.handleUseSignatureChange}
                    onDelayChange={this.handleDelayChange}
                    onShowProInfo={() => {
                        this.props.onSetShowProInfo(true);
                    }}
                    onSelectEmailSequenceTemplate={this.handleSelectEmailSequenceTemplate}
                    allowAddingCustomPlaceholders={!this.isEditingTemplateForAutomation()}
                />
                {this.state.errorMessage ? (
                    <FlashBanner
                        type='error'
                        hasBorder={true}
                        onHidden={() => {
                            this.setState({errorMessage: null});
                        }}
                    >
                        {this.state.errorMessage}
                    </FlashBanner>
                ) : null}
                <div className={styles['footer-bar']}>
                    <FooterBar
                        left={
                            this.isViewingSharedSequenceTemplate() &&
                            this.state.isUsersSequenceDirty ? undefined : (
                                <Button
                                    variant='text'
                                    size='big'
                                    onClick={this.handleCancelButtonPress}
                                    // @ts-expect-error
                                    getButtonRef={(ref) => {
                                        this.cancelButtonRef = ref;
                                    }}
                                >
                                    Cancel
                                </Button>
                            )
                        }
                        right={
                            <StackLegacy isHorizontal={true} spacing={32} alignCenter={true}>
                                {!this.shouldDisableForNonPro() &&
                                (canChangeEmailTemplateGroup ||
                                    this.newIsEditingChildTemplate()) ? (
                                    <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                                        <div>Template group:</div>
                                        <SavedSettingToast state={this.props.toastState}>
                                            <TemplateGroupPicker
                                                isDisabled={!canChangeEmailTemplateGroup}
                                                groups={this.props.groups}
                                                templateGroupId={this.state.templateGroupId}
                                                onChange={(value) => {
                                                    this.setState(
                                                        {templateGroupId: value.id},
                                                        () => {
                                                            // we wanna auto-save this value when editing an existing template\
                                                            if (
                                                                !this.isCreatingNewSequenceTemplate()
                                                            ) {
                                                                const subjectifiedTemplates =
                                                                    this.getSubjectifiedTemplates();
                                                                this.props.setIsSaving(true);

                                                                if (
                                                                    !this.props
                                                                        .emailSequenceTemplate
                                                                )
                                                                    return;

                                                                this.props
                                                                    .updateSequenceTemplate({
                                                                        id: this.state
                                                                            .selectedSequenceId,
                                                                        name: this.state.name,
                                                                        isShared:
                                                                            this.state.isShared,
                                                                        groupId:
                                                                            this.state
                                                                                .templateGroupId ===
                                                                            'null'
                                                                                ? null
                                                                                : this.state
                                                                                      .templateGroupId,
                                                                        emailSequenceTemplateMessages:
                                                                            subjectifiedTemplates,
                                                                    })
                                                                    .then(() => {
                                                                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                                                        this.props.refetchGroups &&
                                                                            this.props.refetchGroups();
                                                                        this.props.setIsSaving(
                                                                            false
                                                                        );
                                                                    });
                                                            }
                                                        }
                                                    );
                                                }}
                                                styles={{
                                                    // @ts-expect-error
                                                    control: (provided) => ({
                                                        ...provided,
                                                        width: '200px',
                                                    }),
                                                }}
                                            />
                                        </SavedSettingToast>
                                    </StackLegacy>
                                ) : null}
                                {this.renderSharedCheckbox()}
                                {this.renderSaveButton()}
                            </StackLegacy>
                        }
                    />
                </div>
                {this.state.isCollectingName ? (
                    <SingleInputModal
                        title='Name this template'
                        subtitle='Give a descriptive name so you can find it later'
                        placeholder='e.g. Trade show followup'
                        initialValue={this.getSequenceName()}
                        isSaving={this.state.isSaving}
                        onSave={this.handleNameCollected}
                        onClose={this.handleNameInputClose}
                    />
                ) : null}
                {this.state.isShowingCloseDialog ? (
                    <Dialog
                        anchor={this.cancelButtonRef}
                        question='Uh oh! You’re about to lose your unsaved changes. Are you sure you’d like to close this and cancel your changes?'
                        primaryText='Yes, cancel changes'
                        secondaryText='No, keep editing'
                        isWarning={true}
                        onPrimary={() => {
                            this.setState({isShowingCloseDialog: false});
                            this.props.closeModal();
                        }}
                        onSecondary={() => {
                            this.setState({isShowingCloseDialog: false});
                        }}
                        location='bottom'
                    />
                ) : undefined}
            </form>
        );
    }

    handleCancelButtonPress = () => {
        if (!this.state.isUsersSequenceDirty) {
            this.props.closeModal();
        }

        this.setState({isShowingCloseDialog: true});
    };

    hasMultipleMessageTemplates() {
        return (
            Object.keys(
                // @ts-expect-error
                this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                    .emailSequenceTemplateMessages
            ).length > 1
        );
    }

    // Non-pro users cannot save sequence templates with follow-ups
    shouldDisableForNonPro() {
        return (
            this.props.isLoading ||
            (!hasEmailSequenceCapability() && this.hasMultipleMessageTemplates())
        );
    }

    renderSaveButton = () => {
        if (this.isViewingSharedSequenceTemplate()) {
            const childSequenceTemplate = this.childSequenceTemplate();
            if (childSequenceTemplate) {
                if (hasEmailSequenceAutomation()) {
                    if (this.state.isUsersSequenceDirty) {
                        return (
                            <React.Fragment>
                                <div
                                    data-tip='View your unsaved draft to save these changes'
                                    data-delay-show='0'
                                >
                                    <SaveButton isSaving={false} disabled={true} />
                                </div>
                            </React.Fragment>
                        );
                    }

                    return (
                        <Button
                            variant='primary'
                            size='big'
                            onClick={() => {
                                this.handleSelectEmailSequenceTemplate(childSequenceTemplate.id);
                            }}
                        >
                            View my version
                        </Button>
                    );
                } else {
                    const newLocation = `/email/templates#/templates/new?sequenceId=${childSequenceTemplate.id}`;

                    return (
                        // @ts-expect-error
                        <Link variant='button-primary' as={RoutedLink} size='big' to={newLocation}>
                            View my version
                        </Link>
                    );
                }
            }

            return (
                <Button
                    variant='save'
                    size='big'
                    wiggle={true}
                    disabled={this.props.isLoading}
                    onClick={this.handleCopySharedTemplateButtonClick}
                >
                    Create your own version to make changes
                </Button>
            );
        }

        if (this.isViewingArchivedSequenceTemplate()) {
            return (
                <Button
                    variant='primary'
                    size='big'
                    disabled={this.props.isLoading}
                    onClick={() => {
                        this.props
                            .undeleteSequenceTemplate(
                                // @ts-expect-error
                                this.state.emailSequenceTemplates[this.state.selectedSequenceId].id
                            )
                            .then(() => {
                                // @ts-expect-error
                                this.setState((prevState) => ({
                                    emailSequenceTemplates: {
                                        ...prevState.emailSequenceTemplates,
                                        [prevState.selectedSequenceId]: {
                                            ...prevState.emailSequenceTemplates[
                                                prevState.selectedSequenceId
                                            ],
                                            isArchived: false,
                                        },
                                    },
                                }));
                            });
                    }}
                >
                    Restore your archived version
                </Button>
            );
        }

        if (this.isEditingOwnSequenceTemplate()) {
            return (
                <SaveButton
                    isSaving={this.state.isSaving}
                    disabled={this.shouldDisableForNonPro()}
                    errorSaving={Boolean(this.state.errorMessage)}
                    onClearSaveComplete={() => {
                        this.props.closeModal();
                    }}
                />
            );
        }

        if (this.isEditingChildTemplate()) {
            return (
                <SaveButton
                    isSaving={this.state.isSaving}
                    disabled={this.shouldDisableForNonPro()}
                    errorSaving={Boolean(this.state.errorMessage)}
                    onClearSaveComplete={() => {
                        this.props.onChildTemplateSaved();
                    }}
                />
            );
        }

        return (
            <Button
                // @ts-expect-error
                getButtonRef={(node) => {
                    this.saveButton = node;
                }}
                variant='save'
                size='big'
                disabled={this.shouldDisableForNonPro()}
                onClick={this.handleSaveButtonClick}
            >
                Save
            </Button>
        );
    };

    renderSharedCheckbox = () => {
        if (this.props.isLoading) return null;

        if (this.shouldDisableForNonPro()) {
            return (
                <div className={styles['disabled-notice']}>
                    This template has follow-ups, which are a{' '}
                    <ProBadgeIcon size={22} className={styles['bottom-icon']} />
                    feature. Please delete follow-ups or{' '}
                    <Link variant='primary' href='/setup/billing/sales' newTab={true}>
                        upgrade your plan
                    </Link>
                    .
                </div>
            );
        }

        // Show disabled checkbox that pops upsell modal if the user does
        // not have access to this feature
        if (!NutClientConfig.hasShareableEmailTemplates) {
            return <ShareableEmailTemplatesUpsell />;
        }

        // If the user is viewing their own template, and there are no siblings
        // for this sequence, show the shared button. User's can't change the
        // share status of a sequence that isn't theres, or for any sequence
        // that has a sibling
        if (
            !this.isViewingSharedSequenceTemplate() &&
            this.state.sortedEmailSequenceTemplates.length <= 1
        ) {
            return (
                <div className='flex flex-grow justify-end align-center'>
                    <Checkbox
                        isLarge={true}
                        checked={this.state.isShared}
                        disabled={
                            // An email sequence template cannot be unshared if
                            // its a child, if it has children, or if its used
                            // for an automation
                            this.isEditingChildTemplate() ||
                            this.isEditingTemplateForAutomation() ||
                            (this.props.emailSequenceTemplate &&
                                this.props.emailSequenceTemplate.children.totalCount > 0)
                        }
                        label='Shared with everyone'
                        onChange={this.handleSharedChange}
                    />
                </div>
            );
        }

        return <div />;
    };

    getActiveTemplate = () => {
        // @ts-expect-error
        return this.state.emailSequenceTemplates[this.state.selectedSequenceId]
            .emailSequenceTemplateMessages[this.state.selectedTemplateId];
    };

    getLastTemplate = () => {
        const emailSequenceTemplateMessages = sortByPosition(
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                .emailSequenceTemplateMessages
        );

        return emailSequenceTemplateMessages[emailSequenceTemplateMessages.length - 1];
    };

    childSequenceTemplate = () => {
        return (
            this.props.emailSequenceTemplate &&
            this.props.emailSequenceTemplate.userChildSequenceTemplate
        );
    };

    handleSelectEmailSequenceTemplate = (emailSequenceTemplateId?: string | null) => {
        if (!emailSequenceTemplateId) return;

        const selectedEmailSequenceTemplate =
            this.state.emailSequenceTemplates[emailSequenceTemplateId];

        if (!selectedEmailSequenceTemplate) return;

        const selectedTemplateId = getFirstEmailTemplateId(selectedEmailSequenceTemplate);
        this.setState({selectedSequenceId: emailSequenceTemplateId, selectedTemplateId});
    };

    handleSubjectChange = (newVal: string) => {
        this.modifyEmailTemplate(this.state.selectedTemplateId, {subject: newVal}, () => {
            this.setState({isUsersSequenceDirty: true});
            this.props.onUserChildTemplateDirtied();
        });
        this.addFakeSubjects();
    };

    handleBodyChange = (newHtml: string, textValue: string) => {
        this.modifyEmailTemplate(
            this.state.selectedTemplateId,
            {
                bodyHTML: newHtml,
                bodyText: textValue,
            },
            () => {
                this.setState({isUsersSequenceDirty: true});
                this.props.onUserChildTemplateDirtied();
            }
        );
    };

    // This does the same thing as handleBodyChange, except that it does not mark the form as dirty,
    // So that we start off pristine, even when the template body loads in for the first time.
    handleBodyLoad = (newHtml: string, textValue: string) => {
        this.modifyEmailTemplate(this.state.selectedTemplateId, {
            bodyHTML: newHtml,
            bodyText: textValue,
        });
    };

    handleDelayChange = (delay: Delay, templateId: string) => {
        this.modifyEmailTemplate(templateId, {delay}, () => {
            this.setState({isUsersSequenceDirty: true});
            this.props.onUserChildTemplateDirtied();
        });
    };

    handleEmailSelect = (selectedTemplateId: string) => {
        this.setState({selectedTemplateId});
    };

    handleSendAsReplyChange = () => {
        // @ts-expect-error
        const prevState = this.getActiveTemplate().sendAsReply;
        this.modifyEmailTemplate(this.state.selectedTemplateId, {sendAsReply: !prevState});
        this.addFakeSubjects();
    };

    handleUseSignatureChange = () => {
        // @ts-expect-error
        const prevState = this.getActiveTemplate().useSignature;
        this.modifyEmailTemplate(this.state.selectedTemplateId, {useSignature: !prevState});
    };

    // This is not used during edits, only when saving a new template
    handleSaveButtonClick = (e: React.SyntheticEvent<any>) => {
        e.preventDefault();
        if (!this.isSequenceValid()) return;

        this.setState({isCollectingName: true});
    };

    handleCopySharedTemplateButtonClick = (e: React.SyntheticEvent<any>) => {
        e.preventDefault();

        if (!hasEmailSequenceAutomation()) {
            // Edit this shared template in a new modal
            this.props.startEditingSharedTemplate();

            return;
        }

        // Copy the email sequence template to the user's editing email sequence
        // template

        // Get the email sequence template to copy
        const sourceEmailSequenceTemplate =
            this.state.emailSequenceTemplates[this.state.selectedSequenceId];
        const newUserEmailSequenceTemplate = createNormalizedSequenceTemplate();

        // Copy over data as needed...
        // @ts-expect-error
        newUserEmailSequenceTemplate.name = sourceEmailSequenceTemplate.name;
        newUserEmailSequenceTemplate.parent = {
            __typename: 'EmailSequenceTemplate',
            // If the copied email sequence has a parent, use that id. Otherwise
            // the copied email sequence is the parent
            // @ts-expect-error
            id: sourceEmailSequenceTemplate.parent
                ? // @ts-expect-error
                  sourceEmailSequenceTemplate.parent.id
                : // @ts-expect-error
                  sourceEmailSequenceTemplate.id,
            creatorUser: {
                __typename: 'User',
                // @ts-expect-error
                id: sourceEmailSequenceTemplate.creatorUser.id,
                // @ts-expect-error
                name: sourceEmailSequenceTemplate.creatorUser.name,
                // @ts-expect-error
                isEnabled: sourceEmailSequenceTemplate.creatorUser.isEnabled,
            },
        };

        // We need to get the list of email templates from the original email
        // sequence template, copy them to a new array, then create an object of
        // those by id.
        // Get the list of IDs
        const emailTemplateKeys = Object.keys(
            // @ts-expect-error
            sourceEmailSequenceTemplate.emailSequenceTemplateMessages
        );
        // Get the array of messages
        const emailTemplateMessages = emailTemplateKeys.map((emailTemplateKey) => {
            // @ts-expect-error
            return sourceEmailSequenceTemplate.emailSequenceTemplateMessages[emailTemplateKey];
        });
        // Copy the data to a new array for the user's email sequence template
        const newUserEmailTemplates = emailTemplateMessages.map(
            // @ts-expect-error
            (emailTemplateMessage: EmailTemplate) => {
                return {
                    ...emailTemplateMessage,
                    id: uniqueId(),
                    delay: {
                        // Copy this, for a new reference
                        ...emailTemplateMessage.delay,
                    },
                    sentCount: 0,
                    openedCount: 0,
                    clickCount: 0,
                };
            }
        );

        const emailTemplatesById = newUserEmailTemplates.reduce<Record<string, any>>(
            (acc, emailTemplate) => {
                acc[emailTemplate.id] = emailTemplate;

                return acc;
            },
            {}
        );

        // @ts-expect-error
        this.setState((prevState) => ({
            selectedSequenceId: newUserEmailSequenceTemplate.id,
            // @ts-expect-error
            selectedTemplateId: newUserEmailTemplates[0].id,
            emailSequenceTemplates: {
                ...prevState.emailSequenceTemplates,
                [newUserEmailSequenceTemplate.id]: {
                    ...newUserEmailSequenceTemplate,
                    emailSequenceTemplateMessages: emailTemplatesById,
                },
            },
            isUsersSequenceDirty: true,
        }));
        this.props.onUserChildTemplateDirtied();
    };

    handleSharedChange = (isShared: boolean) => {
        this.setState({isShared});
    };

    addEmail = () => {
        const lastTemplate = this.getLastTemplate();

        // @ts-expect-error
        if (lastTemplate.error) {
            // @ts-expect-error
            this.modifyEmailTemplate(lastTemplate.id, {showError: true});

            return;
        }

        // When adding a new email, keep their useSignature setting
        // @ts-expect-error
        const useSignature = lastTemplate.useSignature;

        // Stop showing validation errors on this message if fields are cleared in the future
        // @ts-expect-error
        this.modifyEmailTemplate(lastTemplate.id, {showError: false});

        const newId = uniqueId('new');
        // @ts-expect-error
        this.setState((prevState) => ({
            emailSequenceTemplates: {
                ...prevState.emailSequenceTemplates,
                [prevState.selectedSequenceId]: {
                    ...prevState.emailSequenceTemplates[prevState.selectedSequenceId],
                    emailSequenceTemplateMessages: {
                        // @ts-expect-error
                        ...prevState.emailSequenceTemplates[prevState.selectedSequenceId]
                            .emailSequenceTemplateMessages,
                        [newId]: {
                            id: newId,
                            // @ts-expect-error
                            position: lastTemplate.position + 1,
                            delay: {
                                timespan: DEFAULT_DELAY,
                                unit: 'bzd',
                            },
                            subject: '',
                            bodyText: '',
                            bodyHTML: '',
                            sendAsReply: true,
                            useSignature,
                            error: null,
                            showError: false,
                            sentCount: 0,
                            clickCount: 0,
                            openedCount: 0,
                        },
                    },
                },
            },
            selectedTemplateId: newId,
        }));
        this.addFakeSubjects();
    };

    removeEmail = (id: string) => {
        const sortedAllTemplates = sortByPosition(
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                .emailSequenceTemplateMessages
        );
        const indexOfDeletedItem = sortedAllTemplates.findIndex((template) => template.id === id);

        // shouldn't happen, but just to be safe
        if (indexOfDeletedItem === -1) return;

        // Get the list of email templates without the removed template
        const otherEmailTemplates = ramda.omit(
            [id],
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                .emailSequenceTemplateMessages
        );
        const sortedEmailTemplates = sortByPosition(otherEmailTemplates);

        // Reset the position values of each template
        sortedEmailTemplates.forEach((template, index) => {
            template.position = index;
        });

        // If we deleted the last email in the list, select the previous template
        const newSelectionIndex = Math.min(indexOfDeletedItem, sortedEmailTemplates.length - 1);
        // @ts-expect-error
        const newSelectionId = sortedEmailTemplates[newSelectionIndex].id;

        if (indexOfDeletedItem === 0) {
            // @ts-expect-error
            const newFirstId = sortedEmailTemplates[0].id;
            // We deleted the first email template, which didn't have a delay.
            // The new first email template should have its delay removed.
            // @ts-expect-error
            otherEmailTemplates[newFirstId].delay = {
                timespan: 0,
                unit: 'bzd',
            };

            // If the new first email was a reply to the one that was deleted, let's copy over the
            // subject and remove the sendAsReply from the new first one.
            // @ts-expect-error
            if (sortedEmailTemplates[0].sendAsReply) {
                // @ts-expect-error
                otherEmailTemplates[newFirstId].sendAsReply = false;
                // @ts-expect-error
                otherEmailTemplates[newFirstId].subject =
                    // @ts-expect-error
                    this.state.emailSequenceTemplates[
                        this.state.selectedSequenceId
                    ].emailSequenceTemplateMessages[id].subject;
            }
        }

        // @ts-expect-error
        this.setState((prevState) => ({
            emailSequenceTemplates: {
                ...prevState.emailSequenceTemplates,
                [prevState.selectedSequenceId]: {
                    ...prevState.emailSequenceTemplates[prevState.selectedSequenceId],
                    emailSequenceTemplateMessages: otherEmailTemplates,
                },
            },
            selectedTemplateId:
                prevState.selectedTemplateId === id ? newSelectionId : prevState.selectedTemplateId,
        }));
    };

    handleNameCollected = (name: string) => {
        this.setState({name});
    };

    handleNameInputClose = () => {
        this.setState({
            isSaving: false,
            isCollectingName: false,
        });
    };

    modifyEmailTemplate = (
        templateId: string,
        newProperties: any,
        completionHandler: () => void = () => {}
    ) => {
        if (
            // @ts-expect-error
            !this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                .emailSequenceTemplateMessages[templateId]
        )
            return;

        this.setState(
            // @ts-expect-error
            (prevState) => ({
                emailSequenceTemplates: {
                    ...prevState.emailSequenceTemplates,
                    [prevState.selectedSequenceId]: {
                        ...prevState.emailSequenceTemplates[prevState.selectedSequenceId],
                        emailSequenceTemplateMessages: {
                            // @ts-expect-error
                            ...prevState.emailSequenceTemplates[prevState.selectedSequenceId]
                                .emailSequenceTemplateMessages,
                            [templateId]: {
                                // @ts-expect-error
                                ...prevState.emailSequenceTemplates[prevState.selectedSequenceId]
                                    .emailSequenceTemplateMessages[templateId],
                                ...newProperties,
                            },
                        },
                    },
                },
            }),
            completionHandler
        );
    };

    // This goes through all the email templates and if it is marked as `sendAsReply`,
    // it adds another property with the subject line that should be displayed.
    addFakeSubjects = () => {
        // @ts-expect-error
        this.setState((prevState) => ({
            emailSequenceTemplates: {
                ...prevState.emailSequenceTemplates,
                [prevState.selectedSequenceId]: {
                    ...prevState.emailSequenceTemplates[prevState.selectedSequenceId],
                    emailSequenceTemplateMessages: addReplySubjects(
                        // @ts-expect-error
                        prevState.emailSequenceTemplates[prevState.selectedSequenceId]
                            .emailSequenceTemplateMessages
                    ).emailSequenceTemplateMessages,
                },
            },
        }));
    };

    validateTemplate = (template: EmailTemplate) => {
        const validationMessage = this.getTemplateValidationMessage(template);
        if (validationMessage !== template.error) {
            this.modifyEmailTemplate(template.id, {error: validationMessage});
        }
    };

    getTemplateValidationMessage = (template: EmailTemplate) => {
        const missingBody = template.bodyText.trim().length === 0;
        const missingSubject = template.subject.trim().length === 0 && !template.sendAsReply;

        if (missingBody && missingSubject) return 'Please add a subject and a body';
        if (missingBody) return 'Please add a body';
        if (missingSubject) return 'Please add a subject';

        return null;
    };

    isSequenceValid = (): boolean => {
        const emailSequenceTemplateMessages = sortByPosition(
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                .emailSequenceTemplateMessages
        );
        let isValid = true;

        emailSequenceTemplateMessages.forEach((templateMessage) => {
            this.validateTemplate(templateMessage);
            if (templateMessage.error) {
                this.modifyEmailTemplate(templateMessage.id, {showError: true});

                isValid = false;
            }
        });

        return isValid;
    };

    isEditingOwnSequenceTemplate = (): boolean => {
        // @ts-expect-error
        return (
            this.state.emailSequenceTemplates[this.state.selectedSequenceId] &&
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId].id !==
                PLACEHOLDER_ID &&
            // @ts-expect-error
            !this.state.emailSequenceTemplates[this.state.selectedSequenceId].isImmutable
        );
    };

    isCreatingNewSequenceTemplate = (): boolean => {
        // @ts-expect-error
        return (
            this.state.emailSequenceTemplates[this.state.selectedSequenceId] &&
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId].id === PLACEHOLDER_ID
        );
    };

    isViewingSharedSequenceTemplate = (): boolean => {
        return Boolean(
            this.state.emailSequenceTemplates[this.state.selectedSequenceId] &&
                // @ts-expect-error
                this.state.emailSequenceTemplates[this.state.selectedSequenceId].isImmutable
        );
    };

    isViewingArchivedSequenceTemplate = (): boolean => {
        return Boolean(
            this.state.emailSequenceTemplates[this.state.selectedSequenceId] &&
                // @ts-expect-error
                this.state.emailSequenceTemplates[this.state.selectedSequenceId].isArchived
        );
    };

    findTemplateByIndex = (index: number): EmailTemplate | null | undefined => {
        const sortedTemplates = sortByPosition(
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                .emailSequenceTemplateMessages
        );

        return sortedTemplates[index];
    };

    getSequenceName = (): string => {
        const firstTemplate = this.findTemplateByIndex(0);

        return this.state.name === 'Create an email template' && firstTemplate
            ? firstTemplate.subject
            : this.state.name;
    };

    isEditingChildTemplate = (): boolean => {
        const emailSequenceTemplate = this.props.emailSequenceTemplate;
        if (!emailSequenceTemplate) return false;

        // If we have a parent, or current editing sequence template id doesnt
        // match the root sequence template id, its a child
        return Boolean(
            emailSequenceTemplate.parent ||
                emailSequenceTemplate.id !== this.state.selectedSequenceId
        );
    };

    newIsEditingChildTemplate = (): boolean => {
        const emailSequenceTemplate =
            this.state.emailSequenceTemplates[this.state.selectedSequenceId];
        if (!emailSequenceTemplate) return false;

        return Boolean(emailSequenceTemplate.parent);
    };

    isEditingTemplateForAutomation = (): boolean => {
        return Boolean(
            this.props.emailSequenceTemplate && this.props.emailSequenceTemplate.emailAutomation
        );
    };

    getCurrentEmailSequenceTemplateParentId = (): string | null | undefined => {
        if (this.props.emailSequenceTemplate && this.props.emailSequenceTemplate.parent) {
            // If our viewed email sequence template has a parent,
            return this.props.emailSequenceTemplate.parent.id;
        }

        const currentEmailSequenceTemplate =
            this.state.emailSequenceTemplates[this.state.selectedSequenceId];

        if (currentEmailSequenceTemplate && currentEmailSequenceTemplate.parent) {
            // If the current email sequence template has a parent, use that
            // parent id
            return currentEmailSequenceTemplate.parent.id;
        }

        if (
            currentEmailSequenceTemplate &&
            this.props.emailSequenceTemplate &&
            currentEmailSequenceTemplate.id !== this.props.emailSequenceTemplate.id
        ) {
            // If the current viewed email sequence template is not the root
            // email sequence template, the root is the parent
            return this.props.emailSequenceTemplate.id;
        }

        return undefined;
    };

    getSubjectifiedTemplates = (): Array<EmailTemplate> => {
        const sortedTemplates = sortByPosition(
            // @ts-expect-error
            this.state.emailSequenceTemplates[this.state.selectedSequenceId]
                .emailSequenceTemplateMessages
        );

        // Transfer any `replySubject` over to the real subject, so the drip
        // sender has the right text by default.
        // Also scrub placeholder blots out of the html
        const subjectifiedTemplates = sortedTemplates.map((template) => {
            return {
                ...template,
                subject: template.replySubject || template.subject,
                bodyHTML: scrubEmailHtmlPlaceholders(template.bodyHTML),
            };
        });

        return subjectifiedTemplates;
    };

    submitForm = (e: React.SyntheticEvent<any>) => {
        e.preventDefault();

        if (!this.isSequenceValid()) return;

        this.setState({isSaving: true});

        const {emailSequenceTemplate} = this.props;
        const subjectifiedTemplates = this.getSubjectifiedTemplates();

        if (!emailSequenceTemplate) return;

        // Flow needs this
        const parentId = this.getCurrentEmailSequenceTemplateParentId();

        if (this.isEditingOwnSequenceTemplate()) {
            this.props
                .updateSequenceTemplate({
                    id: this.state.selectedSequenceId,
                    name: this.state.name,
                    isShared: this.state.isShared,
                    groupId:
                        this.state.templateGroupId === 'null' ? null : this.state.templateGroupId,
                    emailSequenceTemplateMessages: subjectifiedTemplates,
                })
                .then(() => {
                    this.props.closeModal();
                    this.props.refetchGroups?.();
                })
                .catch((err) => {
                    this.setState({
                        isSaving: false,
                        errorMessage: safelyGetGraphQLErrorMessage(err),
                    });
                });
        } else {
            this.props
                .createSequenceTemplate({
                    name: this.state.name,
                    isShared: this.state.isShared,
                    groupId:
                        this.state.templateGroupId === 'null' ? null : this.state.templateGroupId,
                    parentEmailSequenceTemplateId:
                        hasEmailSequenceAutomation() && parentId ? parentId : undefined,
                    emailSequenceTemplateMessages: subjectifiedTemplates,
                })
                .then(() => {
                    if (!this.isEditingChildTemplate()) {
                        this.props.closeModal();
                    } else {
                        this.setState({isSaving: false});
                    }
                })
                .catch((err) => {
                    this.setState({
                        isSaving: false,
                        errorMessage: safelyGetGraphQLErrorMessage(err),
                    });
                });
        }
    };
}

export function EmailTemplateDesignerFormComponent(props: Props) {
    const [isSaving, setIsSaving] = React.useState(false);

    const {groups} = useGetEmailTemplateGroups({});
    const {user} = useGetSessionUser();
    const {toastState} = useManageSaveState(isSaving, 1000);

    return (
        <EmailTemplateDesignerFormClassComponent
            {...props}
            groups={groups}
            user={user}
            setIsSaving={setIsSaving}
            toastState={toastState}
        />
    );
}
